import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useQuestion} from "../../../../redux/Reducers/QuestionReducer";
import QuestionHeader from "../component/QuestionHeader";
import ListenAudio from "../../../../components/Audio/ListenAudio";
import AudioRecorder from "../../../../components/AudioRecorder";
import {DetButton} from "../../../../components/Form/Button/FormButton";
import useForm from "../../../../hooks/useForm";
import URLS from "../../../../API/Axios/URLS";
import GenericQuestionModal from "../Modal/GenericQuestionModal/GenricQuestionModal";
import {BASE_URL} from "../../../../settings/settings";

function SpeakingPhase({item, index, onNext, isLast, recordData, setRecordData}) {
    const [audioData, setAudioData] = useState(null);

    // If we already have data for this index, load it (though usually we move forward linear)
    useEffect(() => {
        if (recordData[index]) {
            setAudioData(recordData[index]);
        } else {
            setAudioData(null);
        }
    }, [index, recordData]);

    const handleSetAnswer = (data) => {
        setAudioData(data);
        setRecordData(prev => ({...prev, [index]: data}));
    };

    const handleNextClick = () => {
        onNext();
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto items-center">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 flex flex-col gap-4 w-full">
                <h3 className="font-bold text-lg text-blue-900">Part {index + 1}</h3>
                <p className="text-blue-800">Listen to the interlocutor and then speak your answer.</p>

                {/* Interlocutor Audio */}
                {item.audioUrl && (
                    <div className="w-full bg-white p-3 rounded-md shadow-sm flex flex-col items-center gap-2">
                        <span className="text-gray-600 font-medium">Interlocutor:</span>
                        <ListenAudio audio={item.audioUrl} autoPlay/>

                    </div>
                )}
            </div>

            <div className="flex flex-col items-center gap-4 w-full">
                <h4 className="text-lg font-medium text-gray-700">Your Response:</h4>
                <AudioRecorder
                    setAnswer={handleSetAnswer}
                    answer={audioData}
                />

                {audioData && (
                    <div className="mt-2 text-green-600 font-medium">
                        Recorded: {Math.round(audioData.duration)}s
                    </div>
                )}
            </div>

            <div className="flex justify-end mt-8 w-full">
                <DetButton
                    onClick={handleNextClick}
                    disabled={!audioData || audioData.duration < 1} // Ensure some recording exists
                    className="w-40 h-[42px]"
                >
                    {isLast ? "Finish" : "Next Part"}
                </DetButton>
            </div>
        </div>
    );
}

export default function InteractiveSpeaking({className, ...props}) {
    const {id} = useParams();
    const {question, questionActions} = useQuestion();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [recordData, setRecordData] = useState({}); // { 0: data, 1: data, 2: data }
    const [showModal, setShowModal] = useState(false);
    const [resultResponse, setResultResponse] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        questionActions.loadQuestions(id);
    }, [id]);

    // Use form just for standard actions wrapper, though we manually construct payload
    const [form, error, loading, setFormData, actions] = useForm({
        questionId: id,
    });

    if (!question) {
        return <div className="p-8 text-center">Loading Question...</div>;
    }

    // Interactive Speaking usually has 'items' list with 3 parts
    const items = question.items || [];
    const currentItem = items[currentIndex];

    // Total parts
    const totalParts = items.length;

    const handleNext = () => {
        if (currentIndex < totalParts - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setIsProcessing(true);
        // Prepare payload
        // We need to upload audios first
        const payload = {
            questionId: id,
            inputType: 'audio' // or whatever backend expects
        };

        try {
            // Process sequentially to avoid race conditions or overwhelming
            const results = {};

            for (let i = 0; i < totalParts; i++) {
                const rec = recordData[i];
                if (rec && rec.audioBlob) {
                    const textData = await questionActions.convertBlobToText({
                        audioBlob: rec.audioBlob,
                        duration: rec.duration
                    });

                    // textData.data is the text
                    // textData.audioUrl is the relative path
                    results[i] = {
                        text: textData.data,
                        url: BASE_URL.replace(/\/$/, "") + textData.audioUrl,
                        duration: Math.round(rec.duration)
                    };
                }
            }

            // Construct final payload for QuestionSerializer
            // Part 1
            if (results[0]) {
                payload.answer = results[0].text;
                payload.answerUrl = results[0].url;
                payload.time = results[0].duration;
            }
            // Part 2
            if (results[1]) {
                payload.answer2 = results[1].text;
                payload.answerUrl2 = results[1].url;
                payload.time2 = results[1].duration;
            }
            // Part 3
            if (results[2]) {
                payload.answer3 = results[2].text;
                payload.answerUrl3 = results[2].url;
                payload.time3 = results[2].duration;
            }
            if (results[3]) {
                payload.answer4 = results[2].text;
                payload.answerUrl4 = results[2].url;
                payload.time4 = results[2].duration;
            }
            if (results[4]) {
                payload.answer5 = results[2].text;
                payload.answerUrl5 = results[2].url;
                payload.time5 = results[2].duration;
            }

            // Post to backend
            actions.post(URLS.genericQuestionView, () => payload)
                .then(response => {
                    setResultResponse(response?.data);
                    setShowModal(true);
                    setIsSubmitted(true);
                })
                .finally(() => {
                    setIsProcessing(false);
                });

        } catch (e) {
            console.error("Error submitting answer", e);
            alert("Error submitting answers. Please try again.");
            setIsProcessing(false);
        }
    };

    const handleNextQuestion = () => {
        if (question && question.nextQuestionId) {
            navigate(`/practice/interactive-speaking/${question.nextQuestionId}`);
            // Reset states for new question
            setIsSubmitted(false);
            setResultResponse(null);
            setCurrentIndex(0);
            setRecordData({});
            setShowModal(false);
        }
    };

    return (
        <div className={`rounded-lg shadow-lg p-6 bg-white min-h-[600px] ${className}`}>
            <QuestionHeader {...question}
                            timeLimit={150000}

            />

            <div className="py-8">
                {isProcessing ? (
                    <div className="flex flex-col items-center justify-center h-[300px] animate-pulse">
                        <div
                            className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <h3 className="text-xl font-semibold text-gray-700">Submitting your responses...</h3>
                        <p className="text-gray-500 mt-2">Please wait while we evaluate your answers.</p>
                    </div>
                ) : !isSubmitted ? (
                    currentItem ? (
                        <SpeakingPhase
                            key={currentIndex}
                            item={currentItem}
                            index={currentIndex}
                            onNext={handleNext}
                            isLast={currentIndex === totalParts - 1}
                            recordData={recordData}
                            setRecordData={setRecordData}
                        />
                    ) : (
                        <div className="text-center">No items found for this question.</div>
                    )
                ) : (
                    <div
                        className="flex flex-col gap-6 w-full max-w-4xl mx-auto h-[400px] justify-center items-center animate-fadeIn">
                        <div className="bg-green-100 p-8 rounded-full mb-4">
                            <span className="text-6xl">🎉</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">Task Completed!</h2>
                        <p className="text-lg text-gray-600">You have successfully finished the interactive speaking
                            practice.</p>

                        <div className="flex gap-4 mt-8">
                            <DetButton
                                onClick={() => setShowModal(true)}
                                className="w-auto px-6 h-[42px] bg-gray-500 hover:bg-gray-600"
                            >
                                View Results
                            </DetButton>

                            {question.nextQuestionId && (
                                <DetButton
                                    onClick={handleNextQuestion}
                                    className="w-auto px-6 h-[42px] bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    Next Question
                                </DetButton>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination / Progress Dots */}
            {!isSubmitted && !isProcessing && (
                <div className="flex justify-center gap-2 mt-8 opacity-50">
                    {items.map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-2 w-2 rounded-full ${idx === currentIndex ? 'bg-blue-600' : 'bg-gray-300'}`}
                        />
                    ))}
                </div>
            )}

            <GenericQuestionModal
                showModal={showModal}
                setShowModal={setShowModal}
                result={resultResponse}
            />
        </div>
    );
}
